import React from "react";
import { t } from "@auto/services";
import * as echarts from "echarts";

import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import EChart from "@src/reporting/components/EChart";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import {
  GEOJSON_FILES,
  GEOGRAPHY_LEVELS,
} from "@src/reporting/model/reportingConstants.mjs";
import {
  isCorrectionFilter,
  reportingResourceKey,
} from "@src/reporting/model/reportingFilters.mjs";
import {
  count,
  geographyMetrics,
  labelOf,
  metricDefinition,
  metricDisplay,
  metricNumber,
  percent,
} from "@src/reporting/model/reportingFormatters.mjs";
import { loadReportingGeography } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const normalizeGeoJson = (geoJson) => ({
  ...geoJson,
  features: (geoJson?.features || []).map((feature) => {
    const gid = feature?.properties?.gid
      ?? feature?.properties?.GID
      ?? feature?.properties?.id
      ?? feature?.id;

    return {
      ...feature,
      properties: {
        ...(feature.properties || {}),
        name: gid === null || gid === undefined ? "" : String(gid),
      },
    };
  }),
});

const loadGeoJson = async (level) => {
  const file = GEOJSON_FILES[level];
  if (!file) return null;

  const base = String(import.meta.env.BASE_URL || "/").replace(/\/?$/, "/");
  const response = await fetch(`${base}geojson/reporting/${file}`);
  if (!response.ok) return null;

  try {
    return normalizeGeoJson(await response.json());
  } catch {
    return null;
  }
};

const GeographyMap = ({ rows, level, metric, correction, loading, onSelect }) => {
  const [mapName, setMapName] = React.useState("");

  React.useEffect(() => {
    let active = true;
    setMapName("");

    loadGeoJson(level).then((geoJson) => {
      if (!active || !geoJson?.features?.length) return;
      const name = `cep-reporting-${level.toLowerCase()}`;
      echarts.registerMap(name, geoJson);
      setMapName(name);
    });

    return () => {
      active = false;
    };
  }, [level]);

  const rowsByGid = React.useMemo(() => {
    const index = new Map();
    for (const row of rows || []) index.set(String(row.gid), row);
    return index;
  }, [rows]);

  const option = React.useMemo(() => {
    if (!mapName) return null;
    const max = Math.max(1, ...(rows || []).map((row) => metricNumber(row, metric)));
    const metricInfo = metricDefinition(metric, correction);

    return {
      animationDuration: 250,
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const row = rowsByGid.get(String(params.name));
          if (!row) return t("reporting.noData");
          return [
            `<strong>${labelOf(row)}</strong>`,
            `${metricInfo.label}: ${metricDisplay(row[metric], metric, correction)}`,
          ].join("<br/>");
        },
      },
      visualMap: {
        min: 0,
        max,
        left: 12,
        bottom: 8,
        calculable: true,
        text: [t("reporting.high"), t("reporting.low")],
      },
      series: [{
        type: "map",
        map: mapName,
        roam: true,
        selectedMode: false,
        emphasis: { label: { show: false } },
        data: (rows || []).map((row) => ({
          name: String(row.gid),
          value: metricNumber(row, metric),
          row,
        })),
      }],
    };
  }, [mapName, metric, rows, rowsByGid, correction]);

  const handleClick = React.useCallback((params) => {
    if (params?.data?.row) onSelect(params.data.row);
  }, [onSelect]);

  if (!option) return null;

  return (
    <div className={styles.mapPanel}>
      <EChart
        option={option}
        loading={loading}
        onClick={handleClick}
        className={styles.mapChart}
      />
    </div>
  );
};

const voterUtilization = (row) => {
  const voters = Number(row?.registeredVoterCount);
  const capacity = Number(row?.capacity);

  if (!Number.isFinite(voters) || !Number.isFinite(capacity) || capacity <= 0) {
    return null;
  }

  return voters * 100 / capacity;
};

const GeographyDistributionWidget = ({
  filter,
  refreshToken = 0,
  initialLevel = "DEPARTMENT",
  compact = false,
  allowLevelSelection = false,
  allowDrillDown = false,
  showMap = false,
  showTable = false,
  onDrillDown = null,
}) => {
  const correction = isCorrectionFilter(filter);
  const metrics = geographyMetrics(correction);
  const [level, setLevel] = React.useState(initialLevel);
  const [metric, setMetric] = React.useState(metrics[0].value);

  React.useEffect(() => {
    setMetric(metrics[0].value);
  }, [correction]);

  const resource = useReportingResource(
    () => loadReportingGeography(filter, level),
    {
      key: reportingResourceKey("application-geography", filter, level),
      refreshToken,
      initialData: [],
    },
  );

  const rows = resource.data || [];
  const displayRows = React.useMemo(() => {
    if (level !== "DEPARTMENT") return rows;

    return [...rows].sort((a, b) => {
      const aLabel = String(a?.code || a?.name || "");
      const bLabel = String(b?.code || b?.name || "");
      return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [rows, level]);

  const rankedRows = React.useMemo(() => {
    if (level === "DEPARTMENT") {
      // Horizontal ECharts categories render the first entry at the bottom.
      return [...displayRows].reverse();
    }

    return [...rows]
      .sort((a, b) => metricNumber(b, metric) - metricNumber(a, metric))
      .slice(0, compact ? 10 : 30)
      .reverse();
  }, [displayRows, rows, level, metric, compact]);

  const option = React.useMemo(() => {
    const metricInfo = metricDefinition(metric, correction);
    const chartLabelOf = (row) => {
      if (level !== "DEPARTMENT") return labelOf(row);
      return String(row?.name || row?.code || row?.gid || t("common.unnamed"));
    };

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const item = params?.[0]?.data;
          if (!item?.row) return "";
          return `${chartLabelOf(item.row)}<br/>${metricInfo.label}: ${metricDisplay(item.value, metric, correction)}`;
        },
      },
      grid: { left: compact ? 125 : 170, right: 24, top: 14, bottom: 30 },
      xAxis: { type: "value", minInterval: metricInfo.kind === "number" ? 1 : undefined },
      yAxis: {
        type: "category",
        data: rankedRows.map(chartLabelOf),
        axisLabel: { width: compact ? 112 : 156, overflow: "truncate" },
      },
      series: [{
        type: "bar",
        data: rankedRows.map((row) => ({ value: metricNumber(row, metric), row })),
        label: { show: true, position: "right", fontSize: 10 },
        labelLayout: { hideOverlap: false },
      }],
    };
  }, [rankedRows, metric, compact, correction, level]);

  const drill = React.useCallback((row) => {
    if (!allowDrillDown || !row?.gid || !row?.level) return;

    const fieldByLevel = {
      DEPARTMENT: "departmentGid",
      ARRONDISSEMENT: "arrondissementGid",
      COMMUNE: "communeGid",
      SECTION: "sectionGid",
      CONSTITUENCY: "constituencyGid",
      VOTING_CENTER: "votingCenterGid",
    };

    const nextLevelByLevel = {
      DEPARTMENT: "ARRONDISSEMENT",
      ARRONDISSEMENT: "COMMUNE",
      COMMUNE: "SECTION",
      SECTION: "VOTING_CENTER",
      CONSTITUENCY: "COMMUNE",
      VOTING_CENTER: null,
    };

    const field = fieldByLevel[row.level];
    const nextLevel = nextLevelByLevel[row.level];
    if (!field || !nextLevel) return;

    const nextFilter = { ...filter, [field]: String(row.gid) };
    setLevel(nextLevel);
    onDrillDown?.(nextFilter);
  }, [allowDrillDown, filter, onDrillDown]);

  const handleChartClick = React.useCallback((params) => {
    if (params?.data?.row) drill(params.data.row);
  }, [drill]);

  const actions = (
    <div className={styles.inlineActions}>
      {allowLevelSelection ? (
        <TextField
          select
          size="small"
          label={t("common.level")}
          value={level}
          onChange={(event) => setLevel(event.target.value)}
          className={styles.actionSelect}
        >
          {GEOGRAPHY_LEVELS.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </TextField>
      ) : null}
      {!compact && metrics.length > 1 ? (
        <TextField
          select
          size="small"
          label={t("common.metric")}
          value={metric}
          onChange={(event) => setMetric(event.target.value)}
          className={styles.actionSelect}
        >
          {metrics.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </TextField>
      ) : null}
    </div>
  );

  return (
    <ReportingWidget
      title={compact
        ? t("reporting.applicationsByDepartment")
        : t("reporting.applicationGeography")}
      subtitle={compact
        ? t("reporting.applicationsByDepartmentSubtitle")
        : t("reporting.geoDrilldownSubtitle")}
      actions={allowLevelSelection || (!compact && metrics.length > 1) ? actions : null}
      loading={resource.loading}
      error={resource.error}
      empty={resource.hasLoaded && rows.length === 0}
    >
      {showMap ? (
        <GeographyMap
          rows={rows}
          level={level}
          metric={metric}
          correction={correction}
          loading={resource.loading}
          onSelect={drill}
        />
      ) : null}

      <EChart
        option={option}
        loading={resource.loading}
        onClick={allowDrillDown ? handleChartClick : null}
        className={compact ? styles.smallChart : styles.geographyChart}
      />

      {showTable ? (
        <div className={styles.tableWrap}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("common.area")}</TableCell>
                  <TableCell align="right">{t("person.applications")}</TableCell>
                  {!correction ? <TableCell align="right">{t("geo.capacity")}</TableCell> : null}
                  {!correction ? <TableCell align="right">{t("reporting.utilization")}</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((row) => (
                  <TableRow
                    key={`${row.level}-${row.gid}`}
                    hover={allowDrillDown}
                    className={allowDrillDown ? styles.clickableRow : ""}
                    onClick={() => drill(row)}
                  >
                    <TableCell>{labelOf(row)}</TableCell>
                    <TableCell align="right">{count(row.applicationCount)}</TableCell>
                    {!correction ? (
                      <TableCell align="right">
                        {row.capacity === null || row.capacity === undefined ? "—" : count(row.capacity)}
                      </TableCell>
                    ) : null}
                    {!correction ? <TableCell align="right">{percent(voterUtilization(row))}</TableCell> : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : null}
    </ReportingWidget>
  );
};

export default GeographyDistributionWidget;
